import { useContext } from 'react';
import {
  IObjektVy,
  BusinessCollectionCompType,
} from '../../../../../Types/ObjektVy';
import {
  orgperslok,
  orgperslokVariables,
  orgperslok_node_TEDOPOrgPersLok as orgperslok_node,
} from './_QueryTypes/orgperslok';
import UpdateMutation from './OrgPersLokUpdateMutation.gql';
import AddMutation from './OrgPersLokAddMutation.gql';
import {
  OrgPersLokUpdateMutation as IUpdateData,
  OrgPersLokUpdateMutationVariables as IUpdateVariables,
} from './_QueryTypes/OrgPersLokUpdateMutation';
import ObjektVySida from '../../../_Common/Components/BusinessCollectionComponents/ObjektVySida/ObjektVySida';
import { ObjektVyKort } from '../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import ObjektVy from '../../../_Common/Components/BusinessCollectionComponents/ObjektVy/ObjektVy';
import clientAvenyModel from '../../../../../Model/clientAvenyModel';
import {
  IClientBusinessModel,
  IClientAssociationModel,
} from '../../../../../Types/ClientModelTypes';
import useNodeQuery from '../../../../../Components/QueryComponents/NodeQuery/useNodeQuery';
import BusinessDataForm from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import Attribut, {
  toPath,
} from '../../../_Common/Components/BusinessCollectionComponents/Attribut/Attribut';
import { ActiveDatabaseContext } from '../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import OrgPersLokStandardFragment from './OrgPersLokStandardFragment.gql';
import { collectionCompToObjektVy } from '../../../_Common/Components/BusinessCollectionComponents/BusinessCollectionUtils';
import { AddCompType } from '../../../_Common/Const/BusinessConfigs';
import {
  ApolloError,
  useMutation,
  gql as graphql,
  FetchResult,
} from '@apollo/client';
import { ObjektVyFaltLista } from '../../../_Common/Components/BusinessCollectionComponents/ObjektVyFaltLista/ObjektVyFaltLista';
import AttributRad from '../../../_Common/Components/BusinessCollectionComponents/AttributRad/AttributRad';
import { selectedId } from '../../../../../Components/MasterDetail/MasterDetailUtils';

const orgperslokmodel: IClientBusinessModel =
  clientAvenyModel.OP.BusinessModels.OPOrgPersLok;

const query = graphql`
  query orgperslok($id: ID!) {
    node(id: $id) {
      id
      __typename
      ...OrgPersLokStandardFragment
    }
  }
  ${OrgPersLokStandardFragment}
`;

export const OrgPersLokForm = (props: {
  nodeData: orgperslok_node | null | undefined;
  nodeLoading: boolean;
  updateMutation: (values: any) => Promise<void | FetchResult<IUpdateData>>;
  mutationLoading: boolean;
  mutationError?: ApolloError;
  addNewObject?: boolean;
  fullScreen?: boolean;
  onCancel?: () => void;
  onValuesCommited?: () => void;
  isNew?: boolean;
  isNewStartFocus?: string;
  unselect?: () => void;
}) => {
  return (
    <BusinessDataForm<orgperslok_node>
      isNew={props.isNew}
      data={props.nodeData}
      model={orgperslokmodel}
      updateMutation={props.updateMutation}
      addNewObject={props.addNewObject}
      fullScreen={props.fullScreen}
      onCancel={props.onCancel}
      onValuesCommited={props.onValuesCommited}
      mutationError={props.mutationError}
      mutationLoading={props.mutationLoading}
      newFormContext
    >
      <ObjektVy nodeLoading={props.nodeLoading} editImplemented>
        <ObjektVySida
          sidNr={1}
          caption="Organisation"
          columns={1}
          collectionName={orgperslokmodel.CollectionName}
        >
          <ObjektVyKort>
            <OrgPersLokGruppForm />
          </ObjektVyKort>
        </ObjektVySida>
      </ObjektVy>
    </BusinessDataForm>
  );
};

const Standard: BusinessCollectionCompType = props => {
  const { nodeData, loading } = useNodeQuery<
    orgperslok,
    orgperslok_node,
    orgperslokVariables
  >(query, selectedId(props.id));
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const [mutation, result] = useMutation<IUpdateData, IUpdateVariables>(
    UpdateMutation
  );
  const updateMutation = (values: any) =>
    mutation({
      variables: {
        db: activeDatabase || '',
        values: {
          id: props.id,
          _version: nodeData ? nodeData._version : 1,
          ...values,
        },
      },
    });

  return (
    <OrgPersLokForm
      isNew={props.isNew}
      onCancel={props.unselect}
      nodeData={nodeData}
      nodeLoading={loading}
      updateMutation={updateMutation}
      fullScreen
      mutationLoading={result.loading}
      mutationError={result.error}
    />
  );
};

const standard = collectionCompToObjektVy(Standard);

export const OrgPersLokAdd: AddCompType = props => {
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const [mutation, result] = useMutation<IUpdateData, IUpdateVariables>(
    AddMutation
  );
  const updateMutation = (values: any) =>
    mutation({
      variables: {
        db: activeDatabase || '',
        values: {
          ...values,
        },
      },
    });

  return (
    <OrgPersLokForm
      isNew={false}
      nodeData={null}
      nodeLoading={false}
      updateMutation={updateMutation}
      mutationLoading={result.loading}
      mutationError={result.error}
      onCancel={props.onCancel}
      onValuesCommited={props.onValuesCommited}
      addNewObject
    />
  );
};

export const OrgPersLokGruppForm = (props: {
  association?: IClientAssociationModel;
  autoFocus?: boolean;
}) => {
  return (
    <BusinessDataForm<orgperslok_node>
      isNew={false}
      model={orgperslokmodel}
      association={props.association?.AssociationName}
    >
      <ObjektVyFaltLista>
        <Attribut<orgperslok_node>
          name="NamnFE"
          mode="read"
          customDisplayLabel="Namn"
        />

        <AttributRad editContainer>
          <Attribut<orgperslok_node>
            name="ORG_NAMN"
            mode="edit"
            size="medium"
            autoFocus={props.autoFocus}
          />
          <Attribut<orgperslok_node>
            name="BENAMNING"
            mode="edit"
            size="medium"
          />

          <Attribut<orgperslok_node>
            name="FORNAMN"
            mode="edit"
            size="medium"
            autoFocus={props.autoFocus}
          />
          <Attribut<orgperslok_node>
            name="EFTERNAMN"
            mode="edit"
            size="medium"
          />

          <Attribut<orgperslok_node> name="ORG_NR" mode="edit" size="medium" />
          <Attribut<orgperslok_node> name="PERS_NR" mode="edit" size="medium" />
        </AttributRad>

        <Attribut<orgperslok_node> name="ORG_NR" mode="read" size="medium" />
        <Attribut<orgperslok_node> name="PERS_NR" mode="read" size="medium" />

        {/* Adressgruppering */}
        <Attribut<orgperslok_node>
          name={toPath('Adress.KompaktAdress')}
          mode="read"
          customDisplayLabel="Adress"
        />

        <AttributRad editContainer>
          <Attribut<orgperslok_node>
            name={toPath('Adress.ADRESS')}
            mode="edit"
            size="medium"
          />
          <Attribut<orgperslok_node>
            name={toPath('Adress.POSTNR')}
            mode="edit"
            size="medium"
          />
          <Attribut<orgperslok_node>
            name={toPath('Adress.ORT')}
            mode="edit"
            size="medium"
          />
        </AttributRad>
      </ObjektVyFaltLista>
    </BusinessDataForm>
  );
};
export const OrgPersLokVyer: IObjektVy[] = [standard];
